import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Checkbox } from '../../Checkbox';
import { SelectMenuContext } from '../context';
import GroupedList from '../GroupedList';
import { getContainerHeight } from '../helper';
import SelectAll from '../SelectMenu/SelectMenuBody/SelectAll';
import VirtualizedList from '../VirtualizedList';
import styles from '@dtsl/css-select-menu/dist/index.module.css';
function MultiSelectionList(props) {
  let {
    options = [],
    onSelect = () => {},
    isControlButton = false,
    selectAllLabel = 'Select all options',
    unSelectAllLabel = 'Unselect all options',
    selectAllIdentifier,
    isSubFolder = false
  } = props;
  const {
    selectedValues,
    setSelectedValues,
    selectMenuMultiSelectRef,
    placeHolder,
    enableVirtualization
  } = useContext(SelectMenuContext);
  const setValue = (selectedObj, event) => {
    const {
      label,
      key
    } = selectedObj;
    const hasValue = selectedValues.some(item => item.label === label && item.key === key);
    let filteredValues = [];
    if (hasValue) {
      filteredValues = selectedValues.filter(item => !(item.label === label && item.key === key));
    } else {
      filteredValues = [...selectedValues, selectedObj];
    }
    !isControlButton && setSelectedValues(filteredValues);
    fireChangeEvent(filteredValues, event);
  };
  const fireChangeEvent = (filteredValues, event) => {
    if (onSelect, event) {
      onSelect(filteredValues, event);
    }
  };
  const renderOption = (option, index) => {
    let {
      key = index,
      label,
      helpText,
      addtionalInfo = () => {},
      disabled,
      customLabelStyle = {},
      checkboxIdentifier = ''
    } = option;
    const isChecked = selectedValues.some(selectedValue => {
      return selectedValue.label === label && selectedValue.key === key;
    });
    const optionClass = classNames({
      [styles['sib-selectmenu-multi-option']]: true,
      [styles['selected']]: isChecked,
      [styles[option.className]]: !!option.className,
      [styles['locked']]: disabled
    });
    const addInfo = addtionalInfo();
    return /*#__PURE__*/React.createElement("li", {
      key: `${key}_${label}_${index}`,
      className: optionClass,
      role: "option",
      "aria-selected": isChecked ? 'true' : 'false',
      onClick: !disabled ? event => setValue(option, event) : null,
      onKeyDown: event => {
        if (event.key === 'Enter' && !disabled) {
          setValue(option, event);
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      key: key,
      title: label,
      className: styles['sib-selectmenu-option__content'],
      disabled: disabled
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-selectmenu-option__content-left']
    }, /*#__PURE__*/React.createElement(Checkbox, {
      ellipsis: true,
      disabled: disabled,
      label: label,
      checked: isChecked,
      identifier: checkboxIdentifier ? checkboxIdentifier : `sibCheckbox_${key}`,
      labelContainerClassName: styles['sib-checkbox-label'],
      labelClassName: styles['sib-checkbox-title-alias'],
      labelContainerStyle: customLabelStyle,
      className: styles['sib-checkbox-wrapper'],
      variant: "positive",
      onChange: event => {
        event.stopPropagation();
      },
      onClick: e => {
        e.stopPropagation();
      },
      onKeyDown: event => {
        if (event.which === 13 || event.which === 32) {
          event.stopPropagation();
          setValue({
            ...option,
            key
          }, event);
        }
      },
      onFocus: event => {
        event.target.closest('li').classList.add(styles['focused']);
      },
      onBlur: event => {
        event.target.closest('li').classList.remove(styles['focused']);
      }
    }), helpText && /*#__PURE__*/React.createElement("span", {
      className: `${styles['sib-selectmenu-option__helptext']} ${styles['sib-add-space']}`
    }, helpText)), addInfo && /*#__PURE__*/React.createElement("span", {
      className: styles['additional-info']
    }, addtionalInfo())));
  };
  let groupedListOptions = options.map((option, index) => {
    return /*#__PURE__*/React.createElement(GroupedList, {
      option: option,
      itemIndex: index,
      renderOption: renderOption,
      key: index
    });
  });
  const itemRenderer = (index, option) => {
    return /*#__PURE__*/React.createElement(GroupedList, {
      option: option,
      itemIndex: index,
      renderOption: renderOption,
      key: index
    });
  };
  const HeaderItem = () => selectAllLabel ? /*#__PURE__*/React.createElement(SelectAll, {
    placeHolder: placeHolder,
    options: options,
    onSelect: onSelect,
    selectAllLabel: selectAllLabel,
    unSelectAllLabel: unSelectAllLabel,
    selectAllIdentifier: selectAllIdentifier
  }) : null;
  const listHeight = useMemo(() => getContainerHeight(options, isSubFolder, !!selectAllLabel), [options, isSubFolder]);
  const optionsJSX = /*#__PURE__*/React.createElement("ul", {
    className: styles['sib-selectmenu-options'],
    role: "presentation",
    ref: selectMenuMultiSelectRef
  }, !enableVirtualization && HeaderItem(), enableVirtualization ? /*#__PURE__*/React.createElement(VirtualizedList, {
    data: options,
    itemContent: itemRenderer,
    isSubFolder: isSubFolder,
    defaultHeight: listHeight,
    header: HeaderItem
  }) : groupedListOptions);
  return optionsJSX;
}
MultiSelectionList.propTypes = {
  selectAllLabel: PropTypes.string,
  unSelectAllLabel: PropTypes.string,
  selectAllIdentifier: PropTypes.string,
  onSelect: PropTypes.func,
  isControlButton: PropTypes.bool,
  isSubFolder: PropTypes.bool,
  selectMenuType: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    value: PropTypes.any,
    addtionalInfo: PropTypes.func,
    customLabelStyle: PropTypes.object
  }))
};
export default MultiSelectionList;